
import Vue from 'vue';
import Component, { mixins } from 'vue-class-component';
import { PingStatusMixin } from '@/helpers/PingStatusMixin';
import { Prop } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import { BoListPanel } from 'bonomo/dist';
import PaginationBar from '@/components/shared/PaginationBar.vue';
import { BetterDataTableHeader } from '@/model/betterVuetifyInterfaces';
import { ICommunity, ICommunityLocker } from '@/model/Community/communityInterfaces';
import CommunityNav from '@/components/community/widget/CommunityNav.vue';
import LockerStatusChip from '@/components/shared/chips/LockerStatusChip.vue';
import { ELockerStatuses } from '@/model/Locker/lockerInterfaces';
import { IShortLocker } from '@/model/shortInterfaces';

const communityModule = namespace('communityModule');
const lockerModule = namespace('lockerModule');

@Component({
  name: 'CommunityLockers',
  components: { BoListPanel, PaginationBar, CommunityNav, LockerStatusChip },
})
export default class CommunityLockers extends mixins(PingStatusMixin) {
  @Prop()
  protected communityId!: number;

  @communityModule.Getter('community')
  community!: ICommunity | null;

  @communityModule.Getter('communityLockers')
  private communityLockers!: ICommunityLocker[];

  @communityModule.Getter('lockersCurrentPage')
  private lockersCurrentPage!: number;

  @communityModule.Getter('lockersLastPage')
  private lockersLastPage!: number;

  @communityModule.Getter('lockersTotal')
  private lockersTotal!: number;

  @communityModule.Action('fetchCommunityLockers')
  private fetchCommunityLockers!: (payload: { communityId: number; filters?: any }) => Promise<any>;

  @communityModule.Action('fetchCommunity')
  private fetchCommunity!: (communityId: number) => Promise<any>;

  @lockerModule.Getter('lockers')
  private allLockers!: IShortLocker[];

  @lockerModule.Action('fetchLockers')
  private fetchLockers!: () => Promise<any>;

  protected selectedPerPage: number = 20;
  readonly perPageOptions: number[] = [15, 20, 50, 100];
  private pollInterval: any = null;

  created() {
    this.pollInterval = setInterval(this.silentFetch, 60000);
  }

  beforeDestroy() {
    clearInterval(this.pollInterval);
  }
  get communityLockersData(): IShortLocker[] {
    if (!this.allLockers.length) return [];
    return this.allLockers.filter(l => String(l.community_id) === String(this.communityId));
  }

  get maxVersion(): string {
    const source = this.allLockers.length ? this.allLockers : this.communityLockers;
    let max = '';
    for (const each of source) {
      if (each.version && this.isNewerVersion(each.version.split('.'), max.split('.'))) max = each.version;
    }
    return max;
  }

  get headers(): BetterDataTableHeader[] {
    return [
      { text: this.$t('common.id') as string,                              value: 'id',               align: 'start', sortable: true },
      { text: this.$t('common.name') as string,                            value: 'name',             align: 'start', sortable: true },
      { text: this.$t('lockers.lockers.fields.machine') as string,         value: 'machine_number',   align: 'center', sortable: false },
      { text: this.$t('lockers.lockers.fields.version') as string,         value: 'version',          align: 'start', sortable: false },
      { text: this.$t('lockers.lockers.fields.os_version') as string,      value: 'os_version',       align: 'center', sortable: false },
      { text: this.$t('lockers.lockers.fields.os_memory') as string,       value: 'os_cpu',           align: 'center', sortable: false },
      { text: this.$t('common.status') as string,                          value: 'status',           align: 'start', sortable: true },
      { text: this.$t('lockers.lockers.fields.counts.title') as string,    value: 'counts',           align: 'center', sortable: false },
      { text: this.$t('lockers.lockers.fields.last_sync') as string,       value: 'last_ping',        align: 'start', sortable: false },
      { text: '',                                                           value: 'detailButton',     sortable: false },
    ];
  }

  loadData() {
    this.fetchCommunityLockers({ communityId: this.communityId, filters: { page: 1, per_page: 500 } });
    if (!this.allLockers.length) this.fetchLockers();
    if (!this.community || this.community.id !== this.communityId) {
      this.fetchCommunity(this.communityId);
    }
  }

  onPageChange(page: number) {
    this.fetchCommunityLockers({ communityId: this.communityId, filters: { page, per_page: this.selectedPerPage } });
  }

  onPerPageChange(value: number) {
    this.selectedPerPage = value;
    this.fetchCommunityLockers({ communityId: this.communityId, filters: { page: 1, per_page: this.selectedPerPage } });
  }

  convertValueToHuman(fileSizeInBytes: string): string {
    let fileSizeNumber: number = parseInt(fileSizeInBytes);
    let i = -1;
    const byteUnits = ['kB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
    do {
      fileSizeNumber /= 1024;
      i++;
    } while (fileSizeNumber > 1024);
    return Math.max(fileSizeNumber, 0.1).toFixed(1) + byteUnits[i];
  }

  isMaxVersion(item: ICommunityLocker): boolean {
    return !!item.version && item.version === this.maxVersion;
  }

  isNewerVersion(a: string[], b: string[]): boolean {
    if (!a.length) return false;
    if (!b.length) return true;
    if (Number(a[0]) > Number(b[0])) return true;
    if (Number(b[0]) > Number(a[0])) return false;
    return this.isNewerVersion(a.slice(1), b.slice(1));
  }

  versionColor(item: ICommunityLocker): string {
    if (this.isMaxVersion(item)) return 'green';
    if (item.status === ELockerStatuses.Online) return 'red';
    return 'orange';
  }

  osCpuToHuman(osCpu: string): string {
    const regexp = /(Core\(TM\) i\d-\d+[^ ]*)|(celeron\(R\) [^ ]*\d+[^ ]*)/ig;
    const matches = osCpu.match(regexp);
    return matches ? matches[0] : osCpu;
  }

  osVersionToHuman(osVersion: string): string {
    let regexp = /^((Debian) GNU\/Linux (\d+))$/;
    let matches = osVersion.match(regexp);
    if (matches) return matches[2] + ' ' + matches[3];
    regexp = /^((LMDE) (\d+) .+)$/;
    matches = osVersion.match(regexp);
    if (matches) return matches[2] + ' ' + matches[3];
    return osVersion;
  }

  shortDate(d: string | null): string | null {
    return d ? d.slice(2, -3) : null;
  }

  private silentFetch() {
    this.fetchCommunityLockers({ communityId: this.communityId, filters: { page: this.lockersCurrentPage, per_page: this.selectedPerPage } });
  }

  machineNumberDisplay(n: any): string {
    const num = parseInt(String(n).replace(/\./g, ''), 10);
    if (isNaN(num)) return '';
    return num < 10 ? '0' + num : String(num);
  }
}
